import React from "react";
import { Alert, Badge, Group, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { IProduct, ProductConfig } from "types/productManagementTypes";
import { useDeviceDefaultConfig } from "./defaultConfigMatch";
import ConfigStateComparison from "./ConfigStateComparison";
import DeviceDefaultConfigPanel from "./DeviceDefaultConfigPanel";
import DeviceLogErrors from "./DeviceLogErrors";
import OtherConfigEntries from "./OtherConfigEntries";
import { useDeviceConfigs } from "./useDeviceConfigs";
import { ProductFetchStatus } from "./DeviceStatesPanel";

// An ObjectId opens with a 4-byte creation timestamp, which is the only
// creation time older configs have.
export const configCreatedAt = (config: ProductConfig & { _id?: string }): string => {
  if (config.createdAt) return new Date(config.createdAt).toLocaleString();
  const id = typeof config._id === "string" ? config._id : "";
  if (!/^[0-9a-f]{24}$/i.test(id)) return "-";
  return new Date(parseInt(id.slice(0, 8), 16) * 1000).toLocaleString();
};

export const isApplyConfig = (config: ProductConfig): boolean => config.type === "apply";

export const appliedAtLabel = (config: ProductConfig): string => {
  if (config.appliedDate) return new Date(config.appliedDate).toLocaleString();
  return config.applied ? "Applied (time not recorded)" : "Not applied yet";
};

export const appliedState = (
  config: ProductConfig,
): { color: string; label: string; detail: string } =>
  config.applied
    ? {
        color: "green",
        label: "Applied",
        detail: config.appliedDate
          ? `The device confirmed this config on ${new Date(config.appliedDate).toLocaleString()}.`
          : "The device confirmed this config.",
      }
    : {
        color: "yellow",
        label: "Saved, not applied",
        detail:
          "Stored here but not confirmed by the device. It is sent the next time the headset connects.",
      };

interface ConfigOverviewPanelProps {
  config: ProductConfig;
  product: IProduct | null;
  productStatus: ProductFetchStatus;
  otherEntries?: ProductConfig[] | null;
  otherEntriesFailed?: boolean;
  hasSavedConfig?: boolean;
}

const ConfigOverviewPanel: React.FC<ConfigOverviewPanelProps> = ({
  config,
  product,
  productStatus,
  otherEntries,
  otherEntriesFailed,
  hasSavedConfig = true,
}) => {
  const defaultConfigState = useDeviceDefaultConfig(config.pid ?? "", product, productStatus);
  const ownRead = useDeviceConfigs(otherEntries === undefined ? (config.pid ?? "") : "");
  const entries = otherEntries === undefined ? ownRead.otherEntries : otherEntries;
  const entriesFailed = otherEntries === undefined ? ownRead.failed : otherEntriesFailed;
  const matchedDefault =
    defaultConfigState.status === "match" ? defaultConfigState.defaultConfig : null;
  const applied = appliedState(config);

  return (
    <Stack gap="md">
      {!hasSavedConfig && (
        <Alert color="blue" variant="light" title="No config stored for this device">
          The platform has nothing assigned to this PID, so there is no applied config to show or to
          compare against the device state. Create one from the Device settings or Wi-Fi tab; until
          then the device falls back to the matching default config below.
        </Alert>
      )}

      {hasSavedConfig && isApplyConfig(config) && (
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Badge variant="light" color={applied.color}>
            {applied.label}
          </Badge>
          <Text size="xs" c="dimmed">
            {applied.detail}
          </Text>
        </Group>
      )}

      {hasSavedConfig && (
        <Paper withBorder radius="sm" p="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {(
              [
                ["PID", config.pid || "-"],
                ["Default config name", config.defaultConfigName || "-"],
                ["Hardware", config.hardwareModel || "-"],
                ["PUI version", config.puiVersion || "-"],
                ["App version", config.version || "-"],
                ["Type", config.type || "-"],
                ["Applied", config.applied ? "Yes" : "No"],
                ["Created", configCreatedAt(config)],
                ["Last queued", config.date ? new Date(config.date).toLocaleString() : "-"],
                ["Applied at", appliedAtLabel(config)],
              ] as [string, string][]
            ).map(([label, val]) => (
              <Group key={label} gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" w={150} style={{ flexShrink: 0 }}>
                  {label}
                </Text>
                <Text size="sm" style={{ wordBreak: "break-word" }}>
                  {val}
                </Text>
              </Group>
            ))}
          </SimpleGrid>
        </Paper>
      )}

      {config.pid && <DeviceLogErrors pid={config.pid} />}

      {config.pid && <DeviceDefaultConfigPanel deviceConfig={config} state={defaultConfigState} />}

      {/* An os/app/reset entry is an instruction, not a configuration. */}
      {hasSavedConfig && config.pid && isApplyConfig(config) && (
        <ConfigStateComparison
          config={config.config}
          product={product}
          status={productStatus}
          defaultConfig={matchedDefault?.config}
          defaultConfigName={matchedDefault?.defaultConfigName}
          applied={config.applied}
        />
      )}

      {hasSavedConfig && config.pid && !isApplyConfig(config) && (
        <Text size="sm" c="dimmed">
          This entry has type <strong>{config.type || "(none)"}</strong>, not the device&apos;s
          configuration, so there is nothing to compare against the device state.
        </Text>
      )}

      {config.pid && <OtherConfigEntries entries={entries} failed={entriesFailed} />}
    </Stack>
  );
};

export default ConfigOverviewPanel;
